import type { RefObject } from 'react';
import { useState, useRef, useEffect } from 'react';

interface UseDragScrollOptions {
  dragSpeedMultiplier?: number;
  dragThreshold?: number;
}

interface UseDragScrollReturn {
  isDragging: boolean;
  dragThresholdMet: boolean;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
    onMouseMove: (e: React.MouseEvent<HTMLElement>) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
  };
}

export const useDragScroll = (
  containerRef: RefObject<HTMLElement>,
  options: UseDragScrollOptions = {},
): UseDragScrollReturn => {
  const { dragSpeedMultiplier = 1.5, dragThreshold = 5 } = options;
  const [isDragging, setIsDragging] = useState(false);
  const [dragThresholdMet, setDragThresholdMet] = useState(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const dragTimer = useRef<NodeJS.Timeout | null>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (!containerRef.current) return;
    e.stopPropagation();
    setIsDragging(true);
    startX.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
    setDragThresholdMet(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();

    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX.current) * dragSpeedMultiplier;

    if (Math.abs(walk) > dragThreshold) {
      setDragThresholdMet(true);
    }

    containerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (dragTimer.current) clearTimeout(dragTimer.current);
    dragTimer.current = setTimeout(() => setDragThresholdMet(false), 0);
  };

  useEffect(() => {
    return () => {
      if (dragTimer.current) clearTimeout(dragTimer.current);
    };
  }, []);

  return {
    isDragging,
    dragThresholdMet,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
    },
  };
};
